// src/components/common/ActionDropdown.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreVertical, Edit3, Trash2, Flag } from "lucide-react";
import ReportModal from "../report/ReportModal";

const ActionDropdown = ({
  isOwner = false,
  allowEdit = false,
  allowDelete = true,
  allowReport = true,
  onEdit,
  onDelete,
  target,     // 👈 pass the thing being acted on (post, user, comment, etc.)
  type = "post", // 👈 fallback if parent doesn’t specify
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAction = (callback) => {
    setOpen(false);
    callback?.();
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className="p-2 bg-gray-700/40 hover:bg-gray-600/50 rounded-lg border border-gray-600/30 transition-all"
        >
          <MoreVertical size={18} className="text-gray-300" />
        </button>

        {open && (
          <div
            role="menu"
            className="absolute right-0 top-full mt-2 w-44 bg-gray-800 border border-gray-700 rounded-xl shadow-lg z-50"
          >
            <div className="p-1">
              {/* Owner-only actions */}
              {isOwner && allowEdit && (
                <button
                  onClick={() => handleAction(onEdit)}
                  className="w-full px-3 py-2 text-left text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg flex items-center space-x-2"
                >
                  <Edit3 size={16} />
                  <span>{t('actionDropdown.edit')}</span>
                </button>
              )}

              {isOwner && allowDelete && (
                <button
                  onClick={() => handleAction(onDelete)}
                  className="w-full px-3 py-2 text-left text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg flex items-center space-x-2"
                >
                  <Trash2 size={16} />
                  <span>{t('actionDropdown.delete')}</span>
                </button>
              )}

              {/* Report (visible for everyone except owner) */}
              {!isOwner && allowReport && (
                <button
                  onClick={() => {
                    setOpen(false);
                    setReportOpen(true);
                  }}
                  className="w-full px-3 py-2 text-left text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg flex items-center space-x-2"
                >
                  <Flag size={16} />
                  <span>{t('actionDropdown.report')}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        target={target}
        type={type}
      />
    </>
  );
};

export default ActionDropdown;
